import type { EventConnectionResponse } from '@emma/api_client/types';
import { Button, Icon, Link, Modal, Text, Tooltip, useToast } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { TableStoreProvider } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import { useDeleteWorkspaceEventConnection, useWorkspaceEventConnections } from '@/hooks/workspace';
import { useCanWriteById } from '@/hooks/workspace/use_workspace_access';
import { ToastOptions } from '@/utility/ToastOptions';
import { AddEventConnectionForm } from './AddEventConnectionForm';
import { eventConnectionsTableColumns } from './column_definitions/EventConnections';

type Props = {
  workspaceId: number;
};

const description = (
  <Text>
    Event connections deliver workspace events to external destinations such as a Microsoft Teams
    channel or a Pulsar topic.{' '}
    <Link
      href="https://baseplate.legogroup.io/docs/default/component/emma/how_to/event_connections/"
      isExternal
    >
      View documentation
    </Link>
  </Text>
);

const EventConnectionTableContent = ({ workspaceId }: Props) => {
  const canWrite = useCanWriteById(workspaceId);
  const toast = useToast();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const { data, isLoading, isValidating, mutate } = useWorkspaceEventConnections(workspaceId);

  const deleteConnection = useDeleteWorkspaceEventConnection(workspaceId);

  const handleDelete = useCallback(
    async (connectionId: string): Promise<boolean> => {
      try {
        await deleteConnection(connectionId);
        await mutate();
        toast(
          ToastOptions.success(
            'Event Connection Removed',
            'The event connection has been deleted.',
            3000
          )
        );
        return true;
      } catch {
        return false;
      }
    },
    [deleteConnection, mutate, toast]
  );

  const handleAddSuccess = useCallback(async () => {
    setIsAddModalOpen(false);
    await mutate();
  }, [mutate]);

  const columns = useMemo(
    () => eventConnectionsTableColumns(canWrite ? handleDelete : undefined),
    [canWrite, handleDelete]
  );

  const connections = (data?.data?.eventConnections ?? []) as EventConnectionResponse[];

  return (
    <>
      <Table<EventConnectionResponse>
        addButton={
          canWrite
            ? () => (
                <Tooltip label="Add a new event connection" placement="left">
                  <Button
                    borderRightRadius="0px"
                    leftIcon={<Icon as={PlusBold} />}
                    onClick={() => setIsAddModalOpen(true)}
                    size="sm"
                    variant="outline"
                  >
                    Add
                  </Button>
                </Tooltip>
              )
            : undefined
        }
        columns={columns}
        count={connections.length}
        data={connections}
        description={description}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        title="Event Connections"
      />
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>Add Event Connection</Modal.Header>
          <Modal.Body pb={6}>
            <AddEventConnectionForm
              onCancel={() => setIsAddModalOpen(false)}
              onSuccess={handleAddSuccess}
              workspaceId={workspaceId}
            />
          </Modal.Body>
        </Modal.Content>
      </Modal>
    </>
  );
};

export const EventConnectionTable = (props: Props) => (
  <TableStoreProvider initialSorting={[{ desc: true, id: 'createdAt' }]} name="EventConnections">
    <EventConnectionTableContent {...props} />
  </TableStoreProvider>
);
